/**
 * useWebPush — Browser push notification subscription management
 * Handles service worker registration, permission requests, and subscription lifecycle.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
const SW_PATH = '/sw.js';

/** Why this browser could not be subscribed, so Settings can say so */
export const WEB_PUSH_FAILURE = {
  UNSUPPORTED: 'unsupported',
  NOT_CONFIGURED: 'not_configured',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  FAILED: 'failed',
} as const;
export type WebPushFailure = (typeof WEB_PUSH_FAILURE)[keyof typeof WEB_PUSH_FAILURE];

export type WebPushResult = { success: true } | { success: false; reason: WebPushFailure };

export interface UseWebPushResult {
  /** Whether the browser supports push notifications */
  isSupported: boolean;
  /** Current Notification permission state */
  permission: NotificationPermission | 'unsupported';
  /** Whether the user is currently subscribed to push */
  isSubscribed: boolean;
  /** Loading state for subscribe/unsubscribe operations */
  isLoading: boolean;
  /** Request permission and subscribe to push notifications */
  subscribe: () => Promise<WebPushResult>;
  /** Unsubscribe from push notifications */
  unsubscribe: () => Promise<WebPushResult>;
}

/**
 * Convert a URL-safe base64 VAPID key to a Uint8Array for the pushManager.subscribe call
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

export function useWebPush(): UseWebPushResult {
  const { authFetch, isAuthenticated } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    'unsupported',
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  // Check current subscription state on mount
  useEffect(() => {
    if (!isSupported) return;

    setPermission(Notification.permission);

    // Check if already subscribed
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((sub) => setIsSubscribed(sub !== null))
      .catch(() => setIsSubscribed(false));
  }, [isSupported]);

  const subscribe = useCallback(async (): Promise<WebPushResult> => {
    if (!isSupported) return { success: false, reason: WEB_PUSH_FAILURE.UNSUPPORTED };
    if (!VAPID_PUBLIC_KEY) return { success: false, reason: WEB_PUSH_FAILURE.NOT_CONFIGURED };
    if (!isAuthenticated) return { success: false, reason: WEB_PUSH_FAILURE.FAILED };

    setIsLoading(true);
    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'denied') return { success: false, reason: WEB_PUSH_FAILURE.DENIED };
      if (result !== 'granted') return { success: false, reason: WEB_PUSH_FAILURE.DISMISSED };

      // Register service worker
      const registration = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subJson = subscription.toJSON();
      if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
        throw new Error('Invalid push subscription — missing required fields');
      }

      // Send subscription to server
      const res = await authFetch(apiUrl('/api/notifications/web-push/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to register subscription on server');
      }

      setIsSubscribed(true);
      return { success: true };
    } catch (_err) {
      return { success: false, reason: WEB_PUSH_FAILURE.FAILED };
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isAuthenticated, authFetch]);

  const unsubscribe = useCallback(async (): Promise<WebPushResult> => {
    if (!isSupported) return { success: false, reason: WEB_PUSH_FAILURE.UNSUPPORTED };
    if (!isAuthenticated) return { success: false, reason: WEB_PUSH_FAILURE.FAILED };

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return { success: true };
      }

      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from server
      await authFetch(apiUrl('/api/notifications/web-push/unsubscribe'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      setIsSubscribed(false);
      return { success: true };
    } catch (_err) {
      return { success: false, reason: WEB_PUSH_FAILURE.FAILED };
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isAuthenticated, authFetch]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
  };
}
